import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";

const FIELDS = [
  { section: "Identité", fields: ["nom", "siret", "tva_intracom", "telephone", "email", "adresse_facturation"] },
  { section: "Compte bancaire principal — France", fields: ["banque_label", "banque_nom", "iban", "bic"] },
  { section: "Compte bancaire secondaire — Irlande", fields: ["banque_secondary_label", "banque_secondary_nom", "iban_secondary", "bic_secondary"] },
  { section: "Décennale", fields: ["decennale_assureur", "decennale_numero", "decennale_validite"] },
  { section: "RC Pro", fields: ["rc_pro_assureur", "rc_pro_numero", "rc_pro_validite"] },
];

const LABELS = {
  nom: "Nom",
  siret: "SIRET",
  tva_intracom: "N° TVA intracom. (optionnel — ex. FR12810689638)",
  telephone: "Téléphone",
  email: "Email",
  adresse_facturation: "Adresse de facturation",
  banque_label: "Libellé (affiché au client)",
  banque_nom: "Nom de la banque",
  iban: "IBAN",
  bic: "BIC / SWIFT",
  banque_secondary_label: "Libellé (affiché au client)",
  banque_secondary_nom: "Nom de la banque",
  iban_secondary: "IBAN",
  bic_secondary: "BIC / SWIFT",
  decennale_assureur: "Assureur",
  decennale_numero: "N° de police",
  decennale_validite: "Validité",
  rc_pro_assureur: "Assureur",
  rc_pro_numero: "N° de police",
  rc_pro_validite: "Validité",
};

export default function AdminParametres() {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const heroInput = useRef();
  const decennaleInput = useRef();
  const rcProInput = useRef();

  useEffect(() => {
    api
      .get("/admin/parametres")
      .then((r) => setForm(r.data))
      .finally(() => setLoading(false));
  }, []);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const uploadFile = async (file) => {
    const data = new FormData();
    data.append("file", file);
    // Do NOT set Content-Type manually — axios must compute the multipart boundary itself.
    const { data: res } = await api.post("/admin/upload", data);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    return `${BACKEND_URL}${res.url}`;
  };

  const onHeroFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setField("hero_image_url", url);
      toast.success("Image du hero importée — n'oublie pas d'enregistrer");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'import");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onAttestationFile = (field, label) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Format PDF requis");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setField(field, url);
      toast.success(`${label} importée — n'oublie pas d'enregistrer`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'import");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/parametres", form);
      setForm(data);
      toast.success("Paramètres enregistrés");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-serial">Chargement</p>;

  return (
    <form onSubmit={submit} data-testid="admin-parametres-page">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="text-eyebrow mb-3">Paramètres</p>
          <h1 className="h-display text-4xl md:text-5xl">Réglages de l'activité.</h1>
        </div>
      </div>

      {/* HERO SITE */}
      <section className="mb-12">
        <p className="text-eyebrow mb-5">Image du hero (page d'accueil)</p>
        <div className="border-t border-border pt-6 grid md:grid-cols-12 gap-6">
          <div className="md:col-span-5">
            <p className="text-[13.5px] text-muted-foreground leading-relaxed mb-4">
              C'est la grande image qui s'affiche en haut de la page d'accueil. Privilégie une
              photo qui parle immédiatement du métier — détail de bois travaillé, gestes, matière.
              Recommandé : photo horizontale, au moins 2000 × 1200 px.
            </p>
            <div className="space-y-3">
              <input
                value={form.hero_image_url || ""}
                onChange={(e) => setField("hero_image_url", e.target.value)}
                placeholder="URL ou importer une photo"
                className="w-full bg-transparent border-b border-border focus:border-primary py-2.5 text-[13px] outline-none"
                data-testid="param-hero-url"
              />
              <input ref={heroInput} type="file" accept="image/*" className="hidden" onChange={onHeroFile} />
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => heroInput.current?.click()}
                  className="text-eyebrow text-primary link-underline-static"
                  disabled={uploading}
                  data-testid="param-hero-upload"
                >
                  <Upload size={12} className="inline mr-1" /> Importer une photo
                </button>
                {form.hero_image_url && (
                  <button
                    type="button"
                    onClick={() => setField("hero_image_url", "")}
                    className="text-[12px] text-muted-foreground link-underline-static inline-flex items-center gap-1"
                  >
                    <X size={12} /> Revenir à l'image par défaut
                  </button>
                )}
              </div>
            </div>
            <div className="mt-6">
              <label className="text-serial block mb-2">Phrase d'introduction (hero)</label>
              <input
                value={form.hero_eyebrow || ""}
                onChange={(e) => setField("hero_eyebrow", e.target.value)}
                placeholder="Maison Luigi Chauveau · Édition mmxxvi"
                className="w-full bg-transparent border-b border-border focus:border-primary py-2.5 text-[14px] outline-none"
                data-testid="param-hero-eyebrow"
              />
            </div>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <div className="aspect-[16/10] overflow-hidden bg-muted border border-border">
              {form.hero_image_url ? (
                <img src={form.hero_image_url} alt="Hero" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground text-[12px]">
                  Image par défaut (photo de menuiserie)
                </div>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2 text-right">
              Aperçu — appliquera sur luigichauveau.fr
            </p>
          </div>
        </div>
      </section>

      {FIELDS.map((g) => (
        <section key={g.section} className="mb-12">
          <p className="text-eyebrow mb-5">{g.section}</p>
          <div className="grid md:grid-cols-2 gap-6 border-t border-border pt-6">
            {g.fields.map((f) => (
              <div key={f}>
                <label className="text-serial block mb-2">{LABELS[f]}</label>
                <input
                  value={form[f] || ""}
                  onChange={(e) => setField(f, e.target.value)}
                  className="w-full bg-transparent border-b border-border focus:border-primary py-2.5 text-[14.5px] outline-none transition-colors"
                  data-testid={`param-${f}`}
                />
              </div>
            ))}
          </div>
        </section>
      ))}

      {/* Attestations PDF — utilisées dans l'envoi automatique des devis */}
      <section className="mb-12">
        <p className="text-eyebrow mb-5">Attestations (PDF)</p>
        <p className="text-[13px] text-muted-foreground mb-5 max-w-2xl leading-relaxed">
          Importez les PDFs de votre attestation décennale et RC Pro. Vous
          pourrez ensuite les joindre à l'envoi d'un devis en un clic
          (utile pour les cabinets d'expertise).
        </p>
        <div className="grid md:grid-cols-2 gap-6 border-t border-border pt-6">
          {/* Décennale */}
          <div>
            <label className="text-serial block mb-2">Attestation décennale</label>
            <input ref={decennaleInput} type="file" accept="application/pdf" className="hidden" onChange={onAttestationFile("decennale_url", "Décennale")} />
            {form.decennale_url ? (
              <div className="flex items-center gap-4 text-[13.5px]" data-testid="decennale-state">
                <a href={form.decennale_url} target="_blank" rel="noreferrer" className="link-underline-static text-foreground">
                  Voir le PDF actuel ↗
                </a>
                <button
                  type="button"
                  onClick={() => decennaleInput.current?.click()}
                  className="text-eyebrow text-primary link-underline-static"
                  disabled={uploading}
                  data-testid="param-decennale-replace"
                >
                  Remplacer
                </button>
                <button
                  type="button"
                  onClick={() => setField("decennale_url", "")}
                  className="text-[12px] text-muted-foreground link-underline-static inline-flex items-center gap-1"
                >
                  <X size={12} /> Retirer
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => decennaleInput.current?.click()}
                className="text-eyebrow text-primary link-underline-static"
                disabled={uploading}
                data-testid="param-decennale-upload"
              >
                <Upload size={12} className="inline mr-1" /> Importer le PDF
              </button>
            )}
          </div>

          {/* RC Pro */}
          <div>
            <label className="text-serial block mb-2">Attestation RC Pro</label>
            <input ref={rcProInput} type="file" accept="application/pdf" className="hidden" onChange={onAttestationFile("rc_pro_url", "RC Pro")} />
            {form.rc_pro_url ? (
              <div className="flex items-center gap-4 text-[13.5px]" data-testid="rcpro-state">
                <a href={form.rc_pro_url} target="_blank" rel="noreferrer" className="link-underline-static text-foreground">
                  Voir le PDF actuel ↗
                </a>
                <button
                  type="button"
                  onClick={() => rcProInput.current?.click()}
                  className="text-eyebrow text-primary link-underline-static"
                  disabled={uploading}
                  data-testid="param-rcpro-replace"
                >
                  Remplacer
                </button>
                <button
                  type="button"
                  onClick={() => setField("rc_pro_url", "")}
                  className="text-[12px] text-muted-foreground link-underline-static inline-flex items-center gap-1"
                >
                  <X size={12} /> Retirer
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => rcProInput.current?.click()}
                className="text-eyebrow text-primary link-underline-static"
                disabled={uploading}
                data-testid="param-rcpro-upload"
              >
                <Upload size={12} className="inline mr-1" /> Importer le PDF
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="mb-12">
        <p className="text-eyebrow mb-5">Conditions générales</p>
        <textarea
          value={form.conditions_generales || ""}
          onChange={(e) => setField("conditions_generales", e.target.value)}
          className="w-full bg-transparent border border-border p-4 text-[14px] min-h-[160px]"
          data-testid="param-cgv"
        />
      </section>

      <section className="mb-12">
        <p className="text-eyebrow mb-5">Mentions légales</p>
        <textarea
          value={form.mentions_legales || ""}
          onChange={(e) => setField("mentions_legales", e.target.value)}
          className="w-full bg-transparent border border-border p-4 text-[14px] min-h-[160px]"
          data-testid="param-mentions"
        />
      </section>

      <div className="flex justify-end">
        <button type="submit" className="btn-terra" disabled={saving || uploading} data-testid="param-save">
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>
    </form>
  );
}
